/**
 * @fileoverview Reject / Revise dialogs for Morgan's mail drafts (MOR-529).
 *
 * Both actions collect a line of text (optional rejection reason; required
 * revision instructions), so they share a modal treatment instead of inline
 * bars squeezed above the draft. Used by DraftReader and the thread reply
 * composer.
 */

import { useEffect, useState } from "react";
import { Wand2 } from "lucide-react";

import { Button } from "@/ui/primitives/button";
import { Spinner } from "@/ui/primitives/spinner";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";

export function RejectDraftDialog({
  open,
  onOpenChange,
  onConfirm,
  working,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  working?: boolean;
}) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reject this draft?</DialogTitle>
          <DialogDescription>
            The draft is discarded and nothing is sent. The reason is recorded
            in the audit trail and helps Morgan draft better next time.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Reason (optional)"
          autoFocus
        />
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={working}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onConfirm(reason)}
            disabled={working}
          >
            {working && <Spinner className="mr-1.5 size-3.5" />}
            Reject draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReviseDraftDialog({
  open,
  onOpenChange,
  onSubmit,
  working,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (instructions: string) => void;
  working?: boolean;
}) {
  const [instructions, setInstructions] = useState("");
  useEffect(() => {
    if (open) setInstructions("");
  }, [open]);

  const submit = () => {
    if (instructions.trim().length === 0) return;
    onSubmit(instructions);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="size-4 text-primary" />
            Ask Morgan to revise
          </DialogTitle>
          <DialogDescription>
            Morgan rewrites this draft in place — the changes come back as a
            diff for you to review before sending.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          rows={3}
          placeholder="e.g. Warmer tone, mention the July milestones, and ask for bank statements in CSV."
          autoFocus
        />
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={working}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={working || instructions.trim().length === 0}
          >
            {working && <Spinner className="mr-1.5 size-3.5" />}
            Revise
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
